import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import { Button, IconButton, MenuItem, Tooltip, Chip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import { debounce } from "lodash";
import { fetchResourcesByAgentId } from "../../../../../features/slices/Resource";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../store";
import { GlobalModal } from "../../../../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import { useParams, useNavigate } from "react-router-dom";
import Back from "../../../../../components/Back";
import { Resource } from "../../../../../interfaces";
import FormattedDateCell from "../../../../../components/FormattedDateCell";

const AgentResources = () => {
  const [metaModal, setMetaModal] = useState(false);
  const { agentId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Resource[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: "id",
      desc: true,
    },
  ]);
  const navigate = useNavigate();

  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };

  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      try {
        const data = await dispatch(
          fetchResourcesByAgentId(Number(agentId))
        ).unwrap();
        setData(data);
      } catch (error) {
        setIsRefetching(false);
        setIsError(true);
        setIsLoading(false);
      } finally {
        setIsRefetching(false);
        setIsLoading(false);
      }
    }, 300),
    [
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      globalFilter,
      columnFilters,
      dispatch,
      agentId,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<Resource>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() === "active" ? "Active" : "Inactive"}
            color={cell.getValue() === "active" ? "success" : "error"}
          />
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "meta_data",
        header: "Meta",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Button
            variant="outlined"
            onClick={() => handleMetaOpen(cell.getValue<any>())}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },

    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",

    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      sorting: [
        {
          id: "created_at",
          desc: false,
        },
      ],
    },
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: false,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,

    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnFilters,
      globalFilter,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => fetchData()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
  });

  return (
    <>
      <Back />
      <Title>Resources for Agent {agentId}</Title>
      <MaterialReactTable table={table} />

      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Meta Data"
      >
        <JsonEditor
          minWidth={"100%"}
          data={selectedMetadata}
          restrictAdd={true}
          restrictDelete={true}
          restrictEdit={true}
        />
      </GlobalModal>
    </>
  );
};

export default AgentResources;
